import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { fetchData } from '../../../services';
import { TicketForm } from '../../forms/tickets/TicketForm';
import { TicketDetail } from '../../forms/tickets/TicketDetail';
import {
    TICKET_TYPES,
    formatTicketDateTime,
    isClosedTicketStatus,
    ticketApiError,
    ticketStatusChipClass,
    ticketTitle,
    ticketTypeLabel,
} from '../../../utils/tickets';

const FILTERS = [
    { id: 'all', label: 'All' },
    { id: TICKET_TYPES.support, label: 'Support' },
    { id: TICKET_TYPES.featureRequest, label: 'Feature request' },
];

export const TicketsPage = () => {
    const { token, base_url, user, projects, isLoadingCompany } = useAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tickets, setTickets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [formDefaults, setFormDefaults] = useState({
        ticketType: TICKET_TYPES.support,
        projectId: null,
    });

    const isSuperAdmin = user?.is_super_admin === true;

    const loadTickets = useCallback(async () => {
        if (!token || !isSuperAdmin) return;

        try {
            setIsLoading(true);
            setLoadError('');
            const result = await fetchData(token, `${base_url}/apis/admin/tickets`);

            if (Array.isArray(result?.tickets)) {
                setTickets(result.tickets);
                return;
            }

            const message = ticketApiError(result, 'Failed to load tickets');
            setTickets([]);
            setLoadError(message);
            if (window.showAlert) {
                const status = /not authorized/i.test(message) ? 403 : /token/i.test(message) ? 401 : 500;
                window.showAlert(status, message);
            }
        } catch (error) {
            console.error('Error loading tickets:', error);
            const message = error?.msg || 'Error loading tickets';
            setTickets([]);
            setLoadError(message);
            if (window.showAlert) {
                window.showAlert(error?.status || 500, message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [token, base_url, isSuperAdmin]);

    useEffect(() => {
        if (!isSuperAdmin) return;
        loadTickets();
    }, [isSuperAdmin, loadTickets]);

    useEffect(() => {
        const shouldOpen = searchParams.get('new') === '1';
        if (!shouldOpen) return;

        const typeParam = searchParams.get('type');
        const projectParam = Number(searchParams.get('project_id'));
        setFormDefaults({
            ticketType: typeParam === TICKET_TYPES.featureRequest
                ? TICKET_TYPES.featureRequest
                : TICKET_TYPES.support,
            projectId: Number.isInteger(projectParam) && projectParam > 0 ? projectParam : null,
        });
        setIsFormOpen(true);
        setSearchParams({}, { replace: true });
    }, [searchParams, setSearchParams]);

    const counts = useMemo(() => ({
        all: tickets.length,
        [TICKET_TYPES.support]: tickets.filter((ticket) => ticket.ticket_type === TICKET_TYPES.support).length,
        [TICKET_TYPES.featureRequest]: tickets.filter((ticket) => ticket.ticket_type === TICKET_TYPES.featureRequest).length,
    }), [tickets]);

    const visibleTickets = useMemo(() => {
        if (typeFilter === 'all') return tickets;
        return tickets.filter((ticket) => ticket.ticket_type === typeFilter);
    }, [tickets, typeFilter]);

    const handleCreated = (ticket) => {
        if (typeFilter !== 'all' && ticket.ticket_type !== typeFilter) {
            setTypeFilter('all');
        }
        setTickets((prev) => [ticket, ...prev.filter((item) => item.id !== ticket.id)]);
    };

    const openNewTicket = () => {
        setFormDefaults({
            ticketType: TICKET_TYPES.support,
            projectId: null,
        });
        setIsFormOpen(true);
    };

    if (isLoadingCompany) {
        return (
            <div className="page">
                <div className="main-content">
                    <div className="loading-panel">Loading...</div>
                </div>
            </div>
        );
    }

    if (!isSuperAdmin) {
        return (
            <div className="page">
                <div className="main-content">
                    <div className="admin-empty-state">
                        <h2>Access Denied</h2>
                        <p>You don&apos;t have permission to access this page.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="main-content">
                <div className="admin-section-header admin-page-title">
                    <h1 className="admin-page-title">Tickets</h1>
                    <button type="button" className="btn btn-primary admin-btn-add" onClick={openNewTicket}>
                        <FontAwesomeIcon icon={faPlus} className="icon" />
                        New ticket
                    </button>
                </div>

                <div className="admin-tabs">
                    <div className="admin-tabs__list">
                        {FILTERS.map((filter) => (
                            <button
                                key={filter.id}
                                type="button"
                                onClick={() => setTypeFilter(filter.id)}
                                className={`admin-tab${typeFilter === filter.id ? ' admin-tab--active' : ''}`}
                            >
                                {filter.label}
                                <span className={`admin-tab__count${typeFilter === filter.id ? ' admin-tab__count--active' : ''}`}>
                                    {counts[filter.id] ?? 0}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {isLoading ? (
                    <div className="loading-panel">Loading tickets...</div>
                ) : loadError ? (
                    <div className="admin-empty-state">
                        <h3>Unable to load tickets</h3>
                        <p>{loadError}</p>
                    </div>
                ) : visibleTickets.length === 0 ? (
                    <div className="admin-empty-state">
                        <h3>No tickets yet</h3>
                        <p>
                            {typeFilter === 'all'
                                ? 'Submit a support ticket or feature request to get started.'
                                : `No ${ticketTypeLabel(typeFilter).toLowerCase()} submissions yet.`}
                        </p>
                    </div>
                ) : (
                    <div className="table-responsive table-responsive--scroll">
                        <table className="data-table tickets-table">
                            <thead>
                                <tr>
                                    <th className="col-id hide-on-mobile">ID</th>
                                    <th className="col-type">Type</th>
                                    <th className="col-primary">Title</th>
                                    <th className="col-company hide-on-mobile">Project</th>
                                    <th className="col-status">Status</th>
                                    <th className="col-date hide-on-mobile">Created</th>
                                    <th className="col-actions--wide">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleTickets.map((ticket) => {
                                    const project = projects.find((item) => item.id === ticket.project_id);
                                    return (
                                        <tr
                                            key={ticket.id}
                                            className="tickets-table__row"
                                            tabIndex={0}
                                            onClick={() => setSelectedTicket(ticket)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    setSelectedTicket(ticket);
                                                }
                                            }}
                                        >
                                            <td className="col-id hide-on-mobile">#{ticket.id}</td>
                                            <td className="col-type">
                                                <span className={`status-chip status-chip--sm ${ticket.ticket_type === 'support' ? 'status-chip--info' : 'status-chip--accent'}`}>
                                                    {ticketTypeLabel(ticket.ticket_type)}
                                                </span>
                                            </td>
                                            <td className="col-primary">{ticketTitle(ticket)}</td>
                                            <td className="col-company hide-on-mobile">
                                                {ticket.project_id ? (
                                                    <Link
                                                        to={`/project/${ticket.project_id}`}
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        {project ? project.project_name || project.name : `Project #${ticket.project_id}`}
                                                    </Link>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td className="col-status">
                                                <span className={`status-chip status-chip--sm ${ticketStatusChipClass(ticket.status)}`}>
                                                    {ticket.status || 'open'}
                                                </span>
                                            </td>
                                            <td className="col-date hide-on-mobile">
                                                {formatTicketDateTime(ticket.created_at)}
                                            </td>
                                            <td className="col-actions--wide">
                                                {isClosedTicketStatus(ticket.status) && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-action btn-sm btn-secondary"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setSelectedTicket(ticket);
                                                        }}
                                                    >
                                                        View response
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <TicketForm
                    isOpen={isFormOpen}
                    onClose={() => setIsFormOpen(false)}
                    onCreated={handleCreated}
                    defaultTicketType={formDefaults.ticketType}
                    defaultProjectId={formDefaults.projectId}
                />

                {selectedTicket && (
                    <TicketDetail
                        ticket={selectedTicket}
                        onClose={() => setSelectedTicket(null)}
                    />
                )}
            </div>
        </div>
    );
};
